import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";

const EDITOR_API_URL = (import.meta.env.VITE_EDITOR_API_URL || "").replace(/\/api\/?$/, "");

/** An app (Claude, ChatGPT… over MCP) changed the open document (API LiliaMcpWriteTools). */
export interface DocumentChangedExternally {
  source: "mcp";
  /** The app's name as it registered, e.g. "Claude". */
  app: string;
  tool: string;
  blockIds: string[];
  /** The version saved before the app's first change: restoring it undoes them. */
  undoVersionId: string | null;
  at: string;
}

/**
 * The open document's live room (the user, 12 Oct: "signalr should be used when we use the mcp").
 * Joins the document's SignalR group and calls `onChange` when an app changes it. Only this:
 * presence (usePresence) stays off. A failed connection is silent: the editor still works, and
 * an app's change shows at the next load.
 */
export function useLiveDocumentChanges(
  documentId: string | undefined,
  accessTokenFactory: () => Promise<string | undefined>,
  onChange: (evt: DocumentChangedExternally) => void,
  enabled = true,
) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const tokenRef = useRef(accessTokenFactory);
  useEffect(() => {
    tokenRef.current = accessTokenFactory;
  }, [accessTokenFactory]);

  useEffect(() => {
    if (!enabled || !documentId) return;
    let stopped = false;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${EDITOR_API_URL}/hubs/document`, {
        withCredentials: true,
        transport: signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
        accessTokenFactory: async () => (await tokenRef.current()) ?? "",
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.None)
      .build();

    connection.on("DocumentChangedExternally", (evt: DocumentChangedExternally) => onChangeRef.current(evt));
    // Rejoin after a reconnect: groups do not survive a new connection.
    const join = () => connection.invoke("JoinDocument", documentId, "", null).catch(() => {});
    connection.onreconnected(() => void join());

    connection
      .start()
      .then(() => (stopped ? undefined : join()))
      .catch(() => {
        /* offline or hub unavailable: the editor works without it */
      });

    return () => {
      stopped = true;
      void connection.stop().catch(() => {});
    };
  }, [documentId, enabled]);
}
